import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Loader2, LockKeyhole, Mail } from "lucide-react";
import { forgotPassword, consumeLogoutReason, getLoginErrorFeedback, login } from "../services/LoginService";
import FlamexAreaComercialLogo from "../components/FlamexAreaComercialLogo";
import ParticlesBackground from "../components/ParticlesBackground";
import { useAppAlert } from "../context/alert-context";
import "./LoginFlamex.css";

// Mesma composição do site institucional (flamexnet.com.br): uma frase curta por linha, "É" em
// branco, o resto em amarelo — adjetivos na serifada itálica, a marca na sem serifa pesada. A
// digitação percorre as três linhas na ordem.
const HERO_LINES = [
  { lead: "É ", word: "rápido.", face: "serif" },
  { lead: "É ", word: "fácil.", face: "serif" },
  { lead: "É ", word: "Flamex.", face: "sans" },
].map((line) => ({ ...line, text: `${line.lead}${line.word}` }));

let heroOffset = 0;
for (const line of HERO_LINES) {
  line.start = heroOffset;
  heroOffset += line.text.length;
}
const HERO_LENGTH = heroOffset;
const HERO_TITLE = HERO_LINES.map((line) => line.text).join(" ");

// Rede do hero em tons neutros. Cores de marca aqui (pontos âmbar, fios vermelhos) foram
// testadas e vetadas: davam "cara de neon" pesada ao sistema.
const PARTICLE_COLOR = "rgba(255, 255, 255, 0.5)";
const PARTICLE_LINK_COLOR = "rgba(255, 255, 255, 1)";

const REMEMBER_EMAIL_KEY = "flamex.login.remember_email";

const logoutFeedbackByReason = {
  INATIVIDADE: {
    title: "Sessao encerrada",
    message: "Voce foi desconectado por falta de interacao.",
  },
  NOVO_LOGIN: {
    title: "Sessao substituida",
    message: "Sua conta foi acessada em outro dispositivo.",
  },
  ADMINISTRADOR: {
    title: "Sessao encerrada",
    message: "Seu acesso foi encerrado pela administracao.",
  },
  CONTA_INATIVADA: {
    title: "Conta inativada",
    message: "Sua conta foi desativada e o acesso foi encerrado.",
  },
};

function readRememberedEmail() {
  try {
    return localStorage.getItem(REMEMBER_EMAIL_KEY) || "";
  } catch (storageError) {
    return "";
  }
}

export default function Login() {
  const navigate = useNavigate();
  const alert = useAppAlert();
  const [email, setEmail] = useState(readRememberedEmail);
  const [rememberEmail, setRememberEmail] = useState(() => Boolean(readRememberedEmail()));
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typedLength, setTypedLength] = useState(0);

  // O restante do sistema usa zoom global de 80% para ganhar densidade. A tela de login precisa
  // ocupar a viewport real; a classe explícita também cobre navegadores que não aplicam :has().
  useEffect(() => {
    const documentElement = document.documentElement;
    documentElement.classList.add("flamex-login-page");
    return () => documentElement.classList.remove("flamex-login-page");
  }, []);

  useEffect(() => {
    const logoutReason = consumeLogoutReason();
    const feedback = logoutFeedbackByReason[logoutReason];
    if (feedback) {
      alert.warning(feedback.title, feedback.message, {
        position: "top-right",
        duration: 3600,
      });
    }
  }, [alert]);

  useEffect(() => {
    try {
      if (!rememberEmail) {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
        return;
      }
      const normalized = String(email ?? "").trim();
      if (normalized) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, normalized);
      }
    } catch (storageError) {
      /* lembrar e-mail e opcional */
    }
  }, [email, rememberEmail]);

  // Efeito de digitação do slogan.
  useEffect(() => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setTypedLength(HERO_LENGTH);
      return undefined;
    }

    let index = 0;
    const timeoutIds = [];
    const typeNextCharacter = () => {
      index += 1;
      setTypedLength(index);
      if (index < HERO_LENGTH) {
        timeoutIds.push(window.setTimeout(typeNextCharacter, 42));
      }
    };

    timeoutIds.push(window.setTimeout(typeNextCharacter, 350));
    return () => timeoutIds.forEach(window.clearTimeout);
  }, []);

  const handleForgotPassword = async () => {
    if (!email) {
      alert.info("Atenção", "Preencha o e-mail", { position: "bottom-center", duration: 4200 });
      return;
    }

    try {
      await forgotPassword(email);
      alert.success(
        "Solicitação Enviada",
        "Entre em contato com o administrador do sistema para recuperar a senha.",
        { position: "bottom-center", duration: 4200 }
      );
    } catch (requestError) {

    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await login(email, senha);
      alert.success("Login realizado com sucesso", "Acesso liberado ao sistema comercial.", {
        duration: 2400,
      });
      navigate("/hub");
    } catch (requestError) {
      const feedback = getLoginErrorFeedback(requestError);
      const title = feedback?.title || "Acesso negado";
      const message = feedback?.message || "Usuário ou senha incorretos.";
      alert.error(title, message, {
        duration: 3800,
      });
    } finally {
      setLoading(false);
    }
  };

  // A frase inteira é renderizada desde o primeiro quadro; o trecho ainda não digitado fica
  // invisível (visibility: hidden) mas ocupando espaço. Assim as quebras de linha e a altura já
  // são as finais — sem isso a palavra incompleta muda de linha e o bloco, ancorado pela base,
  // dá um salto para cima.
  const heroLines = HERO_LINES.map((line, index) => {
    const typed = Math.min(Math.max(typedLength - line.start, 0), line.text.length);
    const isLast = index === HERO_LINES.length - 1;
    const hasCursor = (typedLength >= line.start && typedLength < line.start + line.text.length)
      || (isLast && typedLength >= HERO_LENGTH);
    return {
      ...line,
      leadTyped: Math.min(typed, line.lead.length),
      wordTyped: Math.max(typed - line.lead.length, 0),
      cursorInLead: hasCursor && typed < line.lead.length,
      cursorInWord: hasCursor && typed >= line.lead.length,
    };
  });

  return (
    <main className="flamex-login">
      <section className="flamex-login__hero">
        <div className="flamex-login__particles">
          <ParticlesBackground
            color={PARTICLE_COLOR}
            linkColor={PARTICLE_LINK_COLOR}
            linkOpacity={0.14}
            quantity={70}
            speed={0.6}
          />
        </div>

        <div className="flamex-login__message">
          <h1 aria-label={HERO_TITLE}>
            {heroLines.map((line) => (
              <span className="flamex-login__line" key={line.word} aria-hidden="true">
                <span>
                  {line.lead.slice(0, line.leadTyped)}
                  {line.cursorInLead && <span className="flamex-login__cursor" />}
                  <span className="flamex-login__ghost">{line.lead.slice(line.leadTyped)}</span>
                </span>
                <mark className={line.face === "sans" ? "is-sans" : undefined}>
                  {line.word.slice(0, line.wordTyped)}
                  {line.cursorInWord && <span className="flamex-login__cursor" />}
                  <span className="flamex-login__ghost">{line.word.slice(line.wordTyped)}</span>
                </mark>
              </span>
            ))}
          </h1>
        </div>
      </section>

      <section className="flamex-login__panel">
        <div className="flamex-login__form-wrap flamex-login__enter">
          <div className="flamex-login__brand">
            <FlamexAreaComercialLogo className="flamex-login__logo" />
          </div>

          <form onSubmit={handleSubmit}>
            <label className="flamex-login__label" htmlFor="login-email">
              E-mail
            </label>
            <div className="flamex-login__field">
              <Mail aria-hidden="true" />
              <input
                id="login-email"
                type="email"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="seuemail@flamex.com.br"
                autoComplete="username"
                autoFocus={!email}
                required
              />
            </div>

            <label className="flamex-login__label" htmlFor="login-password">
              Senha
            </label>
            <div className="flamex-login__field">
              <LockKeyhole aria-hidden="true" />
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="senha"
                value={senha}
                onChange={(event) => setSenha(event.target.value)}
                placeholder="Digite sua senha"
                autoComplete="current-password"
                autoFocus={Boolean(email)}
                required
              />
              <button
                type="button"
                className="flamex-login__password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            <div className="flamex-login__options">
              <label className="flamex-login__remember">
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={(event) => setRememberEmail(event.target.checked)}
                />
                Lembrar e-mail
              </label>
              <button type="button" className="flamex-login__forgot" onClick={handleForgotPassword}>
                Esqueci minha senha
              </button>
            </div>

            <button className="flamex-login__submit" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  Entrar
                  <ArrowRight />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
